import React from 'react';
import type { AttentionItem, HoldInfo } from '../../domain/types';
import { FlowService } from '../../services/flowService';
import { Badge, Pill } from '../../ui/common';
import { JiraCriteriaSection } from './JiraCriteriaSection';
import { JiraSubtasksSection } from './JiraSubtasksSection';
import { linkedPrChipLabelFromAttentionItem } from '../../domain/projectCardIdentity';
import { buildJiraBrowseUrl } from './jiraBrowseUrl';

interface JiraCardExpandedProps {
  item: AttentionItem;
  workflow: string;
  availableTransitions: string[];
  holdInfo: HoldInfo | undefined;
  linkedPullRequests?: AttentionItem[];
  summaryBusy?: boolean;
  onGenerateSummary?: () => void;
  onUpdateWorkflow: (ticket: string, next: string) => void;
  onNextFlow: (e: React.MouseEvent) => void;
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
  onNavigateToGitHub?: (id: string) => void;
  onToggleCriterion: (ticket: string, index: number) => void;
  onAddCriterion: (ticket: string, text: string) => void;
  onUpdateCriterion: (ticket: string, index: number, text: string) => void;
  onDeleteCriterion: (ticket: string, index: number) => void;
  onCycleSubtask: (ticket: string, index: number) => void;
  onOpenDetail?: () => void;
}

function JiraRefChips({
  refs,
  onNavigate,
  isResolved,
}: {
  refs: NonNullable<AttentionItem['refs']>;
  onNavigate: (key: string) => void;
  /** Omit when resolution can't be checked; a chip is always clickable then. */
  isResolved?: (key: string) => boolean;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {refs.map((rf) => {
        const resolved = isResolved ? isResolved(rf.key) : true;
        return (
          <button
            type="button"
            key={rf.key}
            aria-label={resolved ? `Ver ${rf.key}` : `${rf.key}: no está cargado en Zunelo`}
            aria-disabled={!resolved}
            disabled={!resolved}
            title={resolved ? undefined : 'No está cargado en Zunelo'}
            onClick={(e) => {
              if (!resolved) return;
              e.stopPropagation();
              onNavigate(rf.key);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 11px',
              borderRadius: 999,
              cursor: resolved ? 'pointer' : 'not-allowed',
              opacity: resolved ? 1 : 0.55,
              background: 'var(--z-accent-wash)',
              border: 'none',
            }}
          >
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--z-accent-ink)',
              }}
            >
              {rf.key}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--z-accent-copy)' }}>{rf.why}</span>
          </button>
        );
      })}
    </div>
  );
}

function JiraLinkedPullRequestChips({
  pullRequests,
  onNavigate,
}: {
  pullRequests: AttentionItem[];
  onNavigate: (id: string) => void;
}) {
  if (pullRequests.length === 0) return null;
  return (
    <div data-testid="jira-linked-prs" style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {pullRequests.map((pr) => (
        <button
          type="button"
          key={pr.id}
          data-testid={`jira-linked-pr-${pr.id}`}
          aria-label={`Ver ${linkedPrChipLabelFromAttentionItem(pr)}`}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(pr.id);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 11px',
            borderRadius: 999,
            cursor: 'pointer',
            background: 'var(--z-ok-bg, #ecfdf5)',
            border: 'none',
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--z-ok-ink, #065f46)',
            }}
          >
            {linkedPrChipLabelFromAttentionItem(pr)}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--z-muted-2)' }}>{pr.title}</span>
        </button>
      ))}
    </div>
  );
}

function JiraWorkflowControls({
  item,
  workflow,
  availableTransitions,
  onUpdateWorkflow,
  onNextFlow,
}: {
  item: AttentionItem;
  workflow: string;
  availableTransitions: string[];
  onUpdateWorkflow: (ticket: string, next: string) => void;
  onNextFlow: (e: React.MouseEvent) => void;
}) {
  const next = FlowService.getNextWorkflowState(workflow);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
      <label
        style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--z-dim)', whiteSpace: 'nowrap' }}
      >
        Estado:
      </label>
      <select
        value={workflow}
        onChange={(e) => item.ticket && onUpdateWorkflow(item.ticket, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        style={{
          flex: '1 1 auto',
          minWidth: 140,
          height: 32,
          padding: '0 8px',
          borderRadius: 9,
          border: 'none',
          background: 'var(--z-fill)',
          fontFamily: 'inherit',
          fontSize: 12.5,
          color: 'var(--z-text-3)',
        }}
      >
        {availableTransitions.map((step) => (
          <option key={step} value={step}>
            {step}
          </option>
        ))}
      </select>
      {next ? (
        <button
          type="button"
          onClick={onNextFlow}
          style={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            height: 32,
            padding: '0 11px',
            borderRadius: 9,
            background: 'var(--z-accent-soft)',
            color: 'var(--z-accent-ink)',
            border: 'none',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          → {next}
        </button>
      ) : null}
    </div>
  );
}

function JiraHoldBanner({ holdInfo }: { holdInfo: HoldInfo }) {
  return (
    <div
      style={{
        padding: '12px 13px',
        borderRadius: 12,
        background: 'var(--z-plate)',
        border: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
        <span
          style={{
            width: 15,
            height: 15,
            borderRadius: '50%',
            background: holdInfo.kind === 'release' ? 'var(--z-warn)' : 'var(--z-bad)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 800,
            fontSize: 10,
          }}
        >
          {holdInfo.kind === 'release' ? '~' : '!'}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
          {holdInfo.title}
        </span>
        <Pill label={holdInfo.chip} tone={holdInfo.kind === 'release' ? 'warn' : 'bad'} />
      </div>
      <p style={{ margin: '0 0 9px', fontSize: 12, lineHeight: 1.55, color: 'var(--z-muted-3)' }}>
        {holdInfo.note}
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {holdInfo.steps.map((st, sidx) => (
          <Badge key={sidx} label={st.label} tone={st.tone} />
        ))}
      </div>
    </div>
  );
}

export const JiraCardExpanded: React.FC<JiraCardExpandedProps> = ({
  item,
  workflow,
  availableTransitions,
  holdInfo,
  linkedPullRequests = [],
  summaryBusy = false,
  onGenerateSummary,
  onUpdateWorkflow,
  onNextFlow,
  onNavigateToJira,
  isJiraRefResolved,
  onNavigateToGitHub,
  onToggleCriterion,
  onAddCriterion,
  onUpdateCriterion,
  onDeleteCriterion,
  onCycleSubtask,
  onOpenDetail,
}) => {
  const jiraBrowseUrl = buildJiraBrowseUrl(item.ticket, {
    rawSelfUrl: item.rawJiraIssue?.self,
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '12px 13px',
        marginBottom: 12,
        borderRadius: 12,
        background: 'var(--z-fill)',
        border: 'none',
      }}
    >
      {/* 1. AI Summary */}
      <div
        data-testid="jira-expanded-ai"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 11,
          background: 'var(--z-accent-wash)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--z-accent-ink)' }}>
            Resumen IA
          </span>
          {onGenerateSummary ? (
            <button
              type="button"
              data-testid="jira-summary-refresh"
              disabled={summaryBusy}
              onClick={(e) => {
                e.stopPropagation();
                onGenerateSummary();
              }}
              style={{
                marginLeft: 'auto',
                height: 24,
                padding: '0 10px',
                borderRadius: 8,
                border: 'none',
                cursor: summaryBusy ? 'wait' : 'pointer',
                fontSize: 11,
                fontWeight: 600,
                background: 'var(--z-accent-soft)',
                color: 'var(--z-accent-ink)',
                opacity: summaryBusy ? 0.7 : 1,
              }}
            >
              {summaryBusy ? 'Generando…' : 'Resumir'}
            </button>
          ) : null}
        </div>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--z-muted-3)' }}>
          {item.ai}
        </p>
      </div>

      {/* 2. Criterios de Aceptación (AC) */}
      <JiraCriteriaSection
        ticket={item.ticket}
        criteria={item.criteria}
        onToggle={onToggleCriterion}
        onAdd={onAddCriterion}
        onUpdate={onUpdateCriterion}
        onDelete={onDeleteCriterion}
      />

      {/* 3. Workflow Transitions Controls */}
      <JiraWorkflowControls
        item={item}
        workflow={workflow}
        availableTransitions={availableTransitions}
        onUpdateWorkflow={onUpdateWorkflow}
        onNextFlow={onNextFlow}
      />

      {/* 4. Action buttons: Ver detalle & Abrir en Jira */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          paddingTop: 4,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {onOpenDetail ? (
          <button
            type="button"
            data-testid="jira-card-open-detail"
            onClick={onOpenDetail}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 14px',
              borderRadius: 9,
              border: 'none',
              background: 'var(--z-surface)',
              color: 'var(--z-text)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: 'var(--z-sh-2)',
            }}
          >
            <span>⛶</span>
            <span>Ver detalle completo</span>
          </button>
        ) : null}

        {jiraBrowseUrl ? (
          <a
            href={jiraBrowseUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jira-card-open-external"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 32,
              padding: '0 12px',
              borderRadius: 9,
              background: 'transparent',
              color: 'var(--z-muted-2)',
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            <span>Abrir en Jira</span>
            <span style={{ fontSize: 10 }}>↗</span>
          </a>
        ) : null}
      </div>

      {/* 5. References & Linked PRs */}
      {item.refs && item.refs.length > 0 ? (
        <JiraRefChips
          refs={item.refs}
          onNavigate={onNavigateToJira}
          isResolved={isJiraRefResolved}
        />
      ) : null}

      {onNavigateToGitHub ? (
        <JiraLinkedPullRequestChips
          pullRequests={linkedPullRequests}
          onNavigate={onNavigateToGitHub}
        />
      ) : null}

      {holdInfo ? <JiraHoldBanner holdInfo={holdInfo} /> : null}

      <JiraSubtasksSection
        ticket={item.ticket}
        subtasks={item.subtasks}
        onCycleState={onCycleSubtask}
      />
    </div>
  );
};
